import type { JSX } from 'solid-js'
import { closeShop } from '~/lib/close-shop'
import closeIcon from './icons/close.svg'
import { ProgressiveBlur } from './progressive-blur'

// Shop's fixed header and the catalogue's offset under it, shared by the
// real Shop (ShopShell) and the boot skeleton (GLOSSARY.md "Boot skeleton")
// so the reveal's crossfade lands every bar on the element it stood in for.
// Each row's height comes from these classes; the catalogue offsets below
// are their arithmetic.

// The close/balance row: the 42px close button beside the balance pills.
export const HEADER_ROW_CLASS =
  'relative mx-auto flex max-w-[640px] items-center justify-between px-4'
// The tab row, 8px under the close/balance row.
export const TAB_ROW_CLASS =
  'relative mx-auto mt-2 flex max-w-[640px] gap-3 px-4'
// One tab's footprint: 35px tall, at least 80px wide.
export const TAB_SIZE_CLASS = 'h-[35px] min-w-20 px-1'
// The Classic tab's content filter and sort row, flush under the tabs.
export const CLASSIC_CONTROLS_ROW_CLASS =
  'relative mx-auto mt-4 flex max-w-[640px] items-center px-4 pb-3'
export const CLASSIC_CHIP_SIZE_CLASS = 'h-10 rounded-full px-6'
export const CLASSIC_SORT_SIZE_CLASS = 'h-10 w-10'

// The catalogue's column, and its top padding under the fixed header — the
// header's height below the safe area plus 16px of breathing room. The
// header is 8px top padding, the 42px close/balance row, an 8px gap, and the
// 35px tabs: 93px, so 109px. The Classic controls row adds its 16px margin,
// 40px of controls, and 12px bottom padding: 68px more, 177px. Keep in step
// with the rows above and with the currency catalogue's section scroll
// margin.
export const CATALOGUE_COLUMN_CLASS = 'mx-auto max-w-[640px]'
export const CATALOGUE_TOP = 'pt-[calc(109px+var(--safe-area-top))]'
export const CATALOGUE_TOP_UNDER_CLASSIC_CONTROLS =
  'pt-[calc(177px+var(--safe-area-top))]'

/**
 * The fixed header frame: pinned to the viewport over the catalogue, with
 * its blurred gradient backdrop. Fixed, not sticky: a sticky header lost its
 * place while a dialog was open, so the header pins to the viewport and the
 * catalogue below pads for its height (CATALOGUE_TOP_*).
 */
export function ShopHeader(props: { children: JSX.Element }) {
  return (
    <header class="fixed inset-x-0 top-0 z-20 pt-[calc(8px+var(--safe-area-top))]">
      <div
        data-test-id="nav-progressive-blur"
        class="pointer-events-none absolute inset-x-0 bottom-[-20px] top-0 bg-[linear-gradient(180deg,#DDF5EF_47%,rgba(221,245,239,0.65)_87%,rgba(221,245,239,0)_100%)]"
      >
        <ProgressiveBlur strong="top" maxBlur={10} />
      </div>
      {props.children}
    </header>
  )
}

/** Closes Shop: the same button on the real Shop and the boot skeleton. */
export function CloseShopButton() {
  return (
    <button
      type="button"
      aria-label="Close Shop"
      onClick={closeShop}
      class="gradient-outline flex size-[42px] items-center justify-center rounded-full bg-white/55 backdrop-blur-xl"
    >
      <img src={closeIcon} alt="" class="size-5" />
    </button>
  )
}
